import {
  CirclePlus,
  FileDown,
  History,
  LayoutGrid,
  Palette,
  Pencil,
  Share2,
  Star,
} from 'lucide-react-native';
import { memo } from 'react';
import { BiButton } from '../components/ui/BiButton';
import { DashboardMoreMenu } from './DashboardMoreMenu';

/**
 * The header's actions, by what the viewer may do: anyone who sees it may
 * make it their default; editors add cards, reset a custom layout, pick
 * the palette and see the history; the owner also renames and shares.
 * Labels go on wide screens only; on a phone only 添加图表 stays a button
 * and the rest move behind a ⋯.
 */
export const DashboardActions = memo(
  ({
    title,
    compact,
    canEdit,
    canManage,
    onAdd,
    onReset,
    onHistory,
    onPalette,
    onRename,
    onShare,
    onSetDefault,
    onExport,
  }: {
    /** The dashboard's name, captioning the phone's ⋯ menu. */
    title: string;
    compact: boolean;
    canEdit: boolean;
    canManage: boolean;
    onAdd: () => void;
    /** Given when the layout is custom. */
    onReset: (() => void) | null;
    onHistory: () => void;
    onPalette: () => void;
    onRename: () => void;
    onShare: () => void;
    /** Null when it already is the caller's default. */
    onSetDefault: (() => void) | null;
    /** For every viewer once the dashboard is loaded: it as a PDF. */
    onExport: (() => void) | null;
  }) => {
    const setDefault = onSetDefault ? (
      <BiButton
        icon={Star}
        title="设为默认"
        accessibilityLabel="设为默认"
        onPress={onSetDefault}
      />
    ) : null;
    const exportPdf = onExport ? (
      <BiButton
        icon={FileDown}
        title="导出 PDF"
        accessibilityLabel="导出 PDF"
        onPress={onExport}
      />
    ) : null;
    if (!canEdit) {
      if (!onSetDefault && !onExport) return null;
      return compact ? (
        <DashboardMoreMenu
          title={title}
          canEdit={false}
          canManage={false}
          onReset={null}
          onHistory={onHistory}
          onPalette={onPalette}
          onRename={onRename}
          onShare={onShare}
          onSetDefault={onSetDefault}
          onExport={onExport}
        />
      ) : (
        <>
          {setDefault}
          {exportPdf}
        </>
      );
    }
    const add = (
      <BiButton
        icon={CirclePlus}
        title={compact ? undefined : '添加图表'}
        accessibilityLabel="添加图表"
        tone="primary"
        onPress={onAdd}
      />
    );
    if (compact)
      return (
        <>
          {add}
          <DashboardMoreMenu
            title={title}
            canEdit
            canManage={canManage}
            onReset={onReset}
            onHistory={onHistory}
            onPalette={onPalette}
            onRename={onRename}
            onShare={onShare}
            onSetDefault={onSetDefault}
            onExport={onExport}
          />
        </>
      );
    return (
      <>
        {setDefault}
        {onReset ? (
          <BiButton
            icon={LayoutGrid}
            title="重置布局"
            accessibilityLabel="重置布局"
            onPress={onReset}
          />
        ) : null}
        <BiButton
          icon={Palette}
          title="配色"
          accessibilityLabel="看板配色"
          onPress={onPalette}
        />
        <BiButton
          icon={History}
          title="修改记录"
          accessibilityLabel="修改记录"
          onPress={onHistory}
        />
        {exportPdf}
        {canManage ? (
          <>
            <BiButton
              icon={Pencil}
              title="重命名"
              accessibilityLabel="重命名"
              onPress={onRename}
            />
            <BiButton
              icon={Share2}
              title="共享"
              accessibilityLabel="共享"
              onPress={onShare}
            />
          </>
        ) : null}
        {add}
      </>
    );
  },
);
